<template>
  <div>
    <v-container fluid>
      <v-card elevation="3" rounded="xl">
        <v-card-title class="py-4 px-6">
          <v-row class="w-100 ma-0" align="center">
            <v-col cols="12" md="6" class="pa-0">
              <h2 class="text-h5 font-weight-bold primary--text">Plan Subscribers</h2>
              <p class="text-body-2 grey--text ma-0">Users who have an active RMC membership plan</p>
            </v-col>
            <v-col cols="12" md="6" class="pa-0 text-right">
              <v-chip color="primary" outlined class="mr-2">
                <v-icon left small>mdi-account-group</v-icon>
                {{ filteredSubscriptions.length }} Subscribers
              </v-chip>
              <v-btn icon @click="getData" :loading="isLoading">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="mx-6"></v-divider>

        <!-- Filters -->
        <v-card-subtitle class="px-6 pt-4 pb-2">
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                label="Search user or plan..."
                prepend-inner-icon="mdi-magnify"
                outlined rounded dense clearable
                @click:clear="search = ''"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="filterType"
                :items="bookingTypeOptions"
                label="Booking Type"
                outlined rounded dense clearable
              ></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="filterStatus"
                :items="statusOptions"
                label="Status"
                outlined rounded dense clearable
              ></v-select>
            </v-col>
          </v-row>
        </v-card-subtitle>

        <v-card-text class="pa-0">
          <section v-if="isLoading" style="height:300px" class="d-flex align-center justify-center">
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
          </section>

          <v-data-table
            v-else
            :headers="headers"
            :items="filteredSubscriptions"
            hide-default-footer
            :items-per-page="-1"
            class="enhanced-table"
          >
            <template #[`item.sr_no`]="{ index }">
              <span class="font-weight-medium primary--text">{{ index + 1 }}</span>
            </template>

            <!-- User -->
            <template #[`item.user`]="{ item }">
              <div v-if="item.user">
                <div class="font-weight-medium">{{ item.user.first_name }} {{ item.user.last_name }}</div>
                <div class="text-caption grey--text">{{ item.user.mobile || item.user.email || '—' }}</div>
              </div>
              <span v-else class="grey--text">User #{{ item.user_id }}</span>
            </template>

            <!-- Plan -->
            <template #[`item.plan`]="{ item }">
              <div v-if="item.plan">
                <v-chip
                  small
                  :color="item.plan.is_popular ? 'amber darken-2' : 'teal'"
                  dark
                  class="mb-1"
                >
                  {{ item.plan.badge || item.plan.name }}
                </v-chip>
                <div class="text-caption grey--text">{{ item.plan.name }}</div>
              </div>
              <span v-else class="grey--text">—</span>
            </template>

            <!-- Booking Type -->
            <template #[`item.booking_type`]="{ item }">
              <v-chip
                small
                :color="item.booking_type === 'on_demand' ? 'purple' : 'blue'"
                dark outlined
              >
                {{ item.booking_type === 'on_demand' ? 'On-Demand' : 'Dedicated' }}
              </v-chip>
            </template>

            <!-- Usage -->
            <template #[`item.usage`]="{ item }">
              <div style="min-width:140px">
                <div v-if="parseFloat(item.m3_purchased) === 0" class="text-caption">
                  <span class="font-weight-medium">{{ parseFloat(item.m3_used).toFixed(0) }} m³</span>
                  <span class="grey--text ml-1">used · Unlimited</span>
                </div>
                <div v-else>
                  <div class="d-flex justify-space-between text-caption mb-1">
                    <span class="font-weight-medium">{{ parseFloat(item.m3_used).toFixed(0) }} / {{ parseFloat(item.m3_purchased).toFixed(0) }} m³</span>
                    <span :class="item.usage_percentage >= 100 ? 'error--text' : 'grey--text'">
                      {{ item.usage_percentage }}%
                    </span>
                  </div>
                  <v-progress-linear
                    :value="item.usage_percentage"
                    :color="item.usage_percentage >= 100 ? 'error' : item.usage_percentage >= 80 ? 'warning' : 'primary'"
                    rounded
                    height="6"
                    background-color="grey lighten-3"
                  ></v-progress-linear>
                </div>
              </div>
            </template>

            <!-- Period -->
            <template #[`item.period`]="{ item }">
              <div class="text-caption">
                <div>{{ formatDate(item.period_start) }}</div>
                <div class="grey--text">→ {{ formatDate(item.period_end) }}</div>
              </div>
            </template>

            <!-- Status -->
            <template #[`item.status`]="{ item }">
              <v-chip
                small
                :color="item.status === 'active' ? 'success' : 'grey'"
                dark
              >
                {{ item.status }}
              </v-chip>
            </template>

            <!-- Billing cycle -->
            <template #[`item.billing_cycle`]="{ item }">
              <span class="text-caption text-capitalize">{{ item.billing_cycle }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right>
        {{ snackbar.message }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "RmcSubscriptionsPage",
  data() {
    return {
      subscriptions: [],
      isLoading: false,
      search: "",
      filterType: null,
      filterStatus: null,
      snackbar: { show: false, message: "", color: "success" },
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: 50 },
        { text: "User", value: "user", sortable: false },
        { text: "Plan", value: "plan", sortable: false },
        { text: "Type", value: "booking_type", sortable: false },
        { text: "Usage (m³)", value: "usage", sortable: false, width: 180 },
        { text: "Period", value: "period", sortable: false },
        { text: "Billing", value: "billing_cycle", sortable: false },
        { text: "Status", value: "status", sortable: false },
      ],
      bookingTypeOptions: [
        { text: "On-Demand", value: "on_demand" },
        { text: "Dedicated", value: "dedicated" },
      ],
      statusOptions: [
        { text: "Active", value: "active" },
        { text: "Expired", value: "expired" },
        { text: "Cancelled", value: "cancelled" },
      ],
    };
  },
  computed: {
    filteredSubscriptions() {
      let list = this.subscriptions;
      if (this.filterType) {
        list = list.filter((s) => s.booking_type === this.filterType);
      }
      if (this.filterStatus) {
        list = list.filter((s) => s.status === this.filterStatus);
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter((s) => {
          const userName = s.user
            ? `${s.user.first_name || ""} ${s.user.last_name || ""} ${s.user.mobile || ""} ${s.user.email || ""}`.toLowerCase()
            : "";
          const planName = s.plan ? s.plan.name.toLowerCase() : "";
          return userName.includes(q) || planName.includes(q);
        });
      }
      return list;
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const res = await axios.get("rmc_subscriptions");
        this.subscriptions = res.data.data || [];
      } catch (e) {
        this.snackbar = { show: true, message: "Failed to load subscriptions", color: "error" };
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
      } catch {
        return dateStr;
      }
    },
  },
};
</script>
